import { BluetoothStateChanged } from '@/validation/bluetoothStateChanged';
import { FileProcessUpdate } from '@/validation/fileProcessUpdate';
import { HubInfoUpdate } from '@/validation/hubInfoUpdate';
import { Capacitor } from '@capacitor/core';
import { useEffect } from 'react';
import { useLocation } from 'react-router';

import { toast } from '@/components/Toaster/Toast';

import Logger from '@/utils/CapcitorLog';
import { SessionTypeInDatabase } from '@/utils/getFirebaseSessionTypes';

import { sensorService, hubSensorService } from '@/controllers/sensorService';

interface LocationState {
  clientId: string;
  date: string;
  therapistId: string;
  clientName: string | null;
  sessionId: string;
  tests: string[];
  session: SessionTypeInDatabase;
}

type EventListenerToastsProps = {
  useHub?: boolean;
};

//  TODO: Add Sentry to capture errors from the events
export const EventListenerToasts = ({ useHub }: EventListenerToastsProps) => {
  const { state } = useLocation<LocationState | undefined>();
  const inferredIsHubFlow = state?.session?.title === 'Gait Observations';
  const isHubFlow = useHub ?? inferredIsHubFlow;
  const service = isHubFlow ? hubSensorService : sensorService;

  useEffect(() => {
    // 1️⃣ Define the callbacks
    const handleHubInfoUpdate = (event: HubInfoUpdate) => {
      const { type, title, message, displayCondition } = event;
      toast({
        variant: type,
        title,
        description: message,
        duration: 5000,
      });
    };

    const handleFileProcessUpdate = (event: FileProcessUpdate) => {
      const { variant, title, message, displayCondition, progress } = event;
      Logger.info('File process update event received:', event);

      // If the progress is provided don't show the toast
      if (progress !== undefined) return;

      toast({
        variant: variant,
        title,
        description: message,
        duration: 5000,
      });
    };

    const handleBluetoothStateChanged = (event: BluetoothStateChanged) => {
      const { usable, state, message, action } = event;

      // Only show toast when Bluetooth is not usable & is on Native

      if (!usable && Capacitor.isNativePlatform()) {
        let variant: 'success' | 'error' | 'loading' = 'error';

        switch (state) {
          case 'poweredOn':
            variant = 'success';
            break;
          case 'poweredOff':
          case 'unauthorized':
          case 'unsupported':
          case 'resetting':
          case 'unknown':
            variant = 'error';
            break;
        }

        toast({
          variant: variant,
          title: `Bluetooth ${state}`,
          description: message,
          duration: action === 'wait' ? 5000 : 10000,
        });
      }
    };

    // 2️⃣ Subscribe
    service.on('hubInfoUpdate', handleHubInfoUpdate);
    service.on('fileProcessUpdate', handleFileProcessUpdate);
    service.on('bluetoothStateChanged', handleBluetoothStateChanged);

    // 3️⃣ Cleanup
    return () => {
      service.off('hubInfoUpdate', handleHubInfoUpdate);
      service.off('fileProcessUpdate', handleFileProcessUpdate);
      service.off('bluetoothStateChanged', handleBluetoothStateChanged);
    };
  }, [isHubFlow, service]);

  return null;
};
